import type { Constant } from '@pbt/pbt-ui-components'
import { Utils } from '@pbt/pbt-ui-components'

import {
  DeclineReasonToOrderDisplayStatus,
  OrderDisplayStatus,
} from '~/constants/orderStatusChip'
import { PrescriptionStates, PrescriptionType } from '~/constants/prescription'
import type { PrescriptionDeclineReasonType } from '~/constants/PrescriptionWorkflow'
import type { Prescription } from '~/types'
import {
  getPrescriptionType,
  getPrescriptionWorkflowType,
} from '~/utils/prescription'

/** When provided (e.g. from prescription.rxUsages[].rxUsageState), used for per-usage status (e.g. Shipped, Delivered). */
export interface RxUsageStateLike {
  id: string
  name?: string | null
}

export interface OrderStatusInput {
  prescription: Prescription
  prescriptionStates?: Constant[]
  retailOrderStates?: Constant[]
  /** Per-usage state from prescription.rxUsages[].rxUsageState; takes precedence over prescription.stateId / lineItem.orderStateId for Chewy. */
  rxUsageState?: RxUsageStateLike | null
}

/** API rxUsageState.name / order state → order status chip. Supports uppercase (e.g. SHIPPED) and title case. */
const CHEWY_ORDER_STATE_TO_DISPLAY_STATUS: Record<string, OrderDisplayStatus> =
  {
    PENDING: OrderDisplayStatus.PENDING_PHARMACIST_REVIEW,
    Pending: OrderDisplayStatus.PENDING_PHARMACIST_REVIEW,
    DENIED: OrderDisplayStatus.PHARMACIST_DECLINED,
    Denied: OrderDisplayStatus.PHARMACIST_DECLINED,
    'Pharmacist declined': OrderDisplayStatus.PHARMACIST_DECLINED,
    APPROVED: OrderDisplayStatus.APPROVED,
    Approved: OrderDisplayStatus.APPROVED,
    SHIPPED: OrderDisplayStatus.SHIPPED,
    Shipped: OrderDisplayStatus.SHIPPED,
    DELIVERED: OrderDisplayStatus.DELIVERED,
    Delivered: OrderDisplayStatus.DELIVERED,
    CANCELED: OrderDisplayStatus.CANCELED,
    Canceled: OrderDisplayStatus.CANCELED,
    'Pending pharmacist review': OrderDisplayStatus.PENDING_PHARMACIST_REVIEW,
    VALIDATED: OrderDisplayStatus.PENDING_PHARMACIST_REVIEW,
    Validated: OrderDisplayStatus.PENDING_PHARMACIST_REVIEW,
  }

/** API may return rxUsageState.name as uppercase (e.g. "SHIPPED"). Normalize to title case for map lookup. */
const toTitleCase = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()

export const getChewyDisplayStatusFromStateName = (
  stateName: string,
): OrderDisplayStatus | undefined =>
  CHEWY_ORDER_STATE_TO_DISPLAY_STATUS[stateName] ??
  CHEWY_ORDER_STATE_TO_DISPLAY_STATUS[toTitleCase(stateName)]

/**
 * Chewy order chip statuses where an rxUsage row does not participate in fill number (shows "-- / n")
 * and uses placeholder shipping. Extend when product adds more non-fill rows.
 */
export const CHEWY_ORDER_STATUSES_WITHOUT_FILL_NUMBER =
  new Set<OrderDisplayStatus>([OrderDisplayStatus.CANCELED])

/**
 * Maps `rxUsageState.name` to the same `OrderDisplayStatus` as order chips (known API names only).
 */
export function getChewyOrderChipStatusFromRxUsage(
  usage: { rxUsageState?: RxUsageStateLike | null } | null | undefined,
): OrderDisplayStatus | undefined {
  if (usage == null) return undefined
  const name = usage.rxUsageState?.name?.trim()
  if (!name) return undefined
  return getChewyDisplayStatusFromStateName(name)
}

export function isChewyRxUsageCanceledOrder(
  usage: { rxUsageState?: RxUsageStateLike | null } | null | undefined,
): boolean {
  return (
    getChewyOrderChipStatusFromRxUsage(usage) === OrderDisplayStatus.CANCELED
  )
}

export function isChewyRxUsageOrderPendingPharmacistReview(
  usage: { rxUsageState?: RxUsageStateLike | null } | null | undefined,
): boolean {
  return (
    getChewyOrderChipStatusFromRxUsage(usage) ===
    OrderDisplayStatus.PENDING_PHARMACIST_REVIEW
  )
}

/** True when this usage row gets a fill number and real shipping (not canceled / other omitted statuses). */
export function isChewyRxUsageCountedInFillNumber(
  usage: { rxUsageState?: RxUsageStateLike | null } | null | undefined,
): boolean {
  const s = getChewyOrderChipStatusFromRxUsage(usage)
  return s == null || !CHEWY_ORDER_STATUSES_WITHOUT_FILL_NUMBER.has(s)
}

export function isChewyPharmacistDeclined(input: {
  orderDisplayStatus?: OrderDisplayStatus | null
  rxUsageState?: RxUsageStateLike | null
}): boolean {
  if (input.orderDisplayStatus === OrderDisplayStatus.PHARMACIST_DECLINED) {
    return true
  }
  const name = input.rxUsageState?.name?.trim()
  if (!name) return false
  return (
    getChewyDisplayStatusFromStateName(name) ===
    OrderDisplayStatus.PHARMACIST_DECLINED
  )
}

const getDeclinedOrderDisplayStatus = (
  rejectionReason?: string | null,
): OrderDisplayStatus | null => {
  if (!rejectionReason) {
    return OrderDisplayStatus.DECLINED_OTHER
  }

  const rejectionReasonLower = rejectionReason.toLowerCase()
  const matchedEntry = Object.entries(DeclineReasonToOrderDisplayStatus).find(
    ([reasonType]) => {
      const reasonTypeValue = reasonType as PrescriptionDeclineReasonType
      const reasonTypeString = reasonTypeValue.toLowerCase()
      return rejectionReasonLower.includes(reasonTypeString)
    },
  )

  return matchedEntry ? matchedEntry[1] : OrderDisplayStatus.DECLINED_OTHER
}

const getChewyOrderDisplayStatus = (
  prescription: Prescription,
  stateName: string,
  retailOrderStates: Constant[] | undefined,
  rxUsageState: OrderStatusInput['rxUsageState'],
): OrderDisplayStatus | null => {
  if (stateName === PrescriptionStates.CANCELED) {
    return OrderDisplayStatus.CANCELED
  }

  if (stateName === PrescriptionStates.DECLINED) {
    const declinedStatus = getDeclinedOrderDisplayStatus(
      prescription.rejectionReason,
    )
    if (declinedStatus) return declinedStatus
  }

  const usageStateName =
    rxUsageState?.name?.trim() ||
    (rxUsageState?.id &&
      retailOrderStates &&
      Utils.getConstantName(rxUsageState.id, retailOrderStates)) ||
    null
  if (usageStateName) {
    const orderStatus = getChewyDisplayStatusFromStateName(usageStateName)
    if (orderStatus) return orderStatus
  }

  if (
    !usageStateName &&
    prescription.lineItem?.orderStateId &&
    retailOrderStates
  ) {
    const orderStateName = Utils.getConstantName(
      prescription.lineItem.orderStateId,
      retailOrderStates,
    )
    const orderStatus = getChewyDisplayStatusFromStateName(orderStateName)
    if (orderStatus) return orderStatus
  }

  return null
}

/** In-house prescriptions show FILLED only for these workflow states. */
const IN_HOUSE_FILLED_STATES: string[] = [
  PrescriptionStates.APPROVED,
  PrescriptionStates.PRESCRIBED,
  PrescriptionStates.ORDERED,
  PrescriptionStates.SUBMITTED,
]

const getInHouseOrderDisplayStatus = (
  prescription: Prescription,
  stateName: string,
): OrderDisplayStatus | null => {
  if (stateName === PrescriptionStates.CANCELED) {
    return OrderDisplayStatus.CANCELED
  }

  if (stateName === PrescriptionStates.DECLINED) {
    const declinedStatus = getDeclinedOrderDisplayStatus(
      prescription.rejectionReason,
    )
    return declinedStatus ?? OrderDisplayStatus.DECLINED_OTHER
  }

  if (IN_HOUSE_FILLED_STATES.includes(stateName)) {
    return OrderDisplayStatus.FILLED
  }

  return null
}

export const getOrderDisplayStatus = (
  input: OrderStatusInput,
): OrderDisplayStatus | null => {
  const { prescription, prescriptionStates, retailOrderStates, rxUsageState } =
    input

  const { isInHouse, isScriptOut } = getPrescriptionType(
    prescription.prescriptionType,
  )
  if (
    isScriptOut ||
    prescription.prescriptionType === PrescriptionType.SCRIPT_OUT
  ) {
    return null
  }

  const stateName = prescription.stateId
    ? Utils.getConstantName(prescription.stateId, prescriptionStates ?? [])
    : null
  if (!stateName) return null

  const { isChewyActiveRx, isChewyReactiveRx } = getPrescriptionWorkflowType({
    orderType: prescription.type,
    prescriptionType: prescription.prescriptionType,
    origin: prescription.origin,
  })
  const isChewy = isChewyActiveRx || isChewyReactiveRx

  if (isChewy) {
    return getChewyOrderDisplayStatus(
      prescription,
      stateName,
      retailOrderStates,
      rxUsageState,
    )
  }

  if (isInHouse) {
    return getInHouseOrderDisplayStatus(prescription, stateName)
  }

  return null
}
